import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useBlockPacing } from './hooks/useBlockPacing';
import type { BlockPacing } from './blockPacing';
import {
  blockRequiresCompletion,
  buildCourseLessonProgressMap,
  buildLessonNavigationItems,
  findLessonByLocation,
  getBlockCompletionMode,
  isLessonCompleteBlock,
  percentCountWindow,
  getFirstLessonLocation,
  getNextIncompleteLessonLocation,
  getNextLessonLocation,
  getOrderedCourseBlockIds,
  getPreviousLessonLocation,
  selectCursorProgressMeasure,
  selectIsCourseComplete,
  useAccountConfig,
  type CourseBlock,
  type CurrentLocation,
  type ExportedCourse,
  type Lesson,
} from '@course-authoring/shared-types';

/**
 * Core learner-flow state and handlers shared by Course Preview and the
 * Export Player. Encapsulates lesson navigation, per-block completion gating,
 * the Finish-Course transition, and the LMS-resume cursor.
 *
 * Callers layer additional concerns (scroll restoration, server progress
 * persistence, focus management) on top of the returned values.
 */
export interface LearnerCourseFlow {
  pacing: BlockPacing;
  currentLocation: CurrentLocation | null;
  setCurrentLocation: (location: CurrentLocation | null) => void;
  completionStatus: Record<string, boolean>;
  markBlockComplete: (blockId: string) => void;

  /** Lesson IDs the learner has engaged with (first scroll-intent/interaction). */
  engagedLessonIds: Record<string, true>;
  /**
   * Mark the currently-active lesson as engaged (idempotent). Flips it to
   * `in_progress` independently of block completion. Wired to `CourseCanvas`'s
   * `onEngage`.
   */
  markCurrentLessonEngaged: () => void;

  currentLesson: Lesson | null;
  currentBlocks: CourseBlock[];
  lessonProgressMap: ReturnType<typeof buildCourseLessonProgressMap>;
  navigationLessons: ReturnType<typeof buildLessonNavigationItems>;

  nextLesson: CurrentLocation | null;
  previousLesson: CurrentLocation | null;

  /** True when every required block in every lesson is marked complete. */
  allLessonsComplete: boolean;
  /** True when the learner has clicked "Finish Course" and is on the completion view. */
  isCourseComplete: boolean;
  /** True after the learner has *ever* completed the course (used for "completed" sidebar styling). */
  hasCompletedCourse: boolean;
  /** True when the current lesson's required blocks are complete. */
  currentLessonCanProceed: boolean;
  /** True when Next/Finish should be enabled. */
  canNavigateNext: boolean;
  /** True when Previous should be enabled. */
  canNavigatePrev: boolean;
  /** Banner-state flag: last lesson, course already complete, but completion view not yet shown. */
  shouldShowCompletedCourseLessonState: boolean;

  /**
   * Index into `getOrderedCourseBlockIds(course)` of the furthest block the
   * learner has reached. `-1` until any block has been observed/completed.
   * Monotonic — only increases.
   */
  furthestReachedIndex: number;
  /** Cursor-based progress in [0.0, 1.0]. Maps to LMS `percentComplete`. */
  progressMeasure: number;
  /**
   * Block ID the canvas should scroll to on the next paint. Set by
   * `hydrateProgress`. Consumers clear it via `clearPendingScrollBlockId`
   * once they've performed the seek.
   */
  pendingScrollBlockId: string | null;
  clearPendingScrollBlockId: () => void;

  /**
   * Mark a block as the furthest-reached. Idempotent and monotonic — never
   * moves the cursor backward. Called by the canvas IntersectionObserver as
   * blocks enter the viewport.
   */
  advanceCursor: (blockId: string) => void;

  /**
   * Seed flow state from an LMS-reported `percentComplete` (0–100).
   *
   * Auto-completes every block at-or-before the resolved cursor (so lesson
   * gating doesn't lock the learner out), advances the cursor monotonically,
   * places `currentLocation` on the lesson containing the cursor, and queues
   * a block-level scroll seek via `pendingScrollBlockId`.
   */
  hydrateProgress: (
    percentComplete: number,
    options?: {
      /**
       * When true, a 100% resume enters the course-completion view. Standalone
       * lesson SCORM resumes set this false so the lesson opens normally while
       * still unblocking completed progress.
       */
      completeCourse?: boolean;
      /** When false, hydrate completion without scrolling to the saved cursor. */
      scrollToCursor?: boolean;
    }
  ) => void;
  /**
   * Seed flow state from per-lesson LMS progress. This is used by SCORM
   * programs where Hoot returns one UserLesson row per CAP lesson.
   */
  hydrateLessonProgress: (
    lessonPercentById: Record<string, number>,
    options?: {
      /**
       * When false, an all-lessons-complete hydration records the prior
       * completion (`hasCompletedCourse`) without entering the completion
       * view — the completed-relaunch review state, where the learner lands
       * on a lesson with progress intact and the course-complete banner.
       */
      completeCourse?: boolean;
      /**
       * Lesson to land on after hydration. Defaults to the lesson containing
       * the furthest hydrated block.
       */
      preferredLessonId?: string | null;
      /** When false, hydrate completion without scrolling to a block cursor. */
      scrollToCursor?: boolean;
    }
  ) => void;

  handleLessonSelect: (lessonId: string) => void;
  handlePrevious: () => void;
  /** Returns true when the call transitioned into the completion view. */
  handleNext: () => boolean;
  /**
   * Handler for the "Continue" button on a Lesson Completion block when
   * playing inside a course. Jumps to the next incomplete lesson (scanning
   * forward from the current lesson and wrapping to the start of the course);
   * if every other lesson is already complete, transitions into the
   * course-completion view. Returns true when that transition occurs.
   */
  handleLessonCompleteContinue: () => boolean;
  /**
   * True when the learner clicked Continue on the final Lesson Complete block
   * with all content done but the course time requirement (NA-153) unmet —
   * the caller shows the "time requirement not met" view instead of the
   * completion screen. Cleared by any navigation, and resolves to the
   * completion state automatically once the gate is satisfied.
   */
  isWaitingForTimeGate: boolean;
  /**
   * True once the learner has clicked Continue on the final Lesson Complete
   * block at least once and been sent to the "time requirement not met" view
   * (NA-153) — unlike `isWaitingForTimeGate`, this never clears on
   * navigation. Distinguishes "actually attempted to finish" from merely
   * having all lesson content marked done while still on a first pass, so
   * review-phase UI (the sticky in-lesson timer bar) doesn't appear before
   * the learner has ever tried to finish the course.
   */
  hasWaitedForTimeGate: boolean;
}

/**
 * Course time-requirement gate (NA-153). Absence-based exemption: callers
 * that never pass one (authoring Preview) are unconditionally ungated by
 * construction. The player passes a fresh object identity on each refresh
 * tick while the gate is unmet so `allLessonsComplete` re-evaluates as time
 * accrues.
 */
export interface FlowTimeGate {
  timeGateSatisfied(): boolean;
}

export interface UseLearnerCourseFlowOptions {
  timeGate?: FlowTimeGate;
  /** Launch/assignment identity, independent of content language. */
  attemptKey?: string;
}

/**
 * Forward boundary resolution (NA-187): how many leading blocks of `blockIds`
 * to treat as reached for a stored `measure`. Resolves the floor-send
 * ambiguity window to its MAXIMUM so a learner never lands behind gated
 * content they already cleared — but never walks *through* a Lesson Complete
 * block: blocks in the provable window `[0, minCount)` are always reached, and
 * the ambiguity zone `[minCount, maxCount)` stops at the first gate. So
 * hydration can clear a gating block sitting at the boundary yet can never
 * finish a lesson (and so never complete the course or fire completion
 * side-effects) the learner had not finished.
 */
function gateAwareReachedCount(
  blockIds: readonly string[],
  measure: number,
  blockById: ReadonlyMap<string, CourseBlock>
): number {
  const { minCount, maxCount } = percentCountWindow(measure, blockIds.length);
  // A fully complete percent proves every block was cleared.
  if (minCount >= blockIds.length) return maxCount;

  // The percent records the furthest block reached, not whether it was
  // cleared, so a gated Single Choice at or past the last provably reached
  // block may still be unanswered. Stop before it; the server's completion
  // record reopens it.
  for (let i = Math.max(0, minCount - 1); i < maxCount; i++) {
    const block = blockById.get(blockIds[i] ?? '');
    if (!block) continue;
    if (block.type === 'single-choice-answer' && block.gated === true) return i;
    if (i >= minCount && isLessonCompleteBlock(block)) return i;
  }
  return maxCount;
}

export function useLearnerCourseFlow(
  course: ExportedCourse,
  options: UseLearnerCourseFlowOptions = {}
): LearnerCourseFlow {
  const { timeGate } = options;
  const { enforceFullVideoView } = useAccountConfig();
  const initialLocation = useMemo(
    () => getFirstLessonLocation(course.lessons),
    [course.lessons]
  );

  const orderedBlockIds = useMemo(() => getOrderedCourseBlockIds(course), [course]);
  const blockIndexById = useMemo(() => {
    const map = new Map<string, number>();
    orderedBlockIds.forEach((id, idx) => map.set(id, idx));
    return map;
  }, [orderedBlockIds]);
  const blockById = useMemo(() => {
    const map = new Map<string, CourseBlock>();
    course.blocks.forEach((block) => map.set(block.id, block));
    return map;
  }, [course.blocks]);

  const [currentLocation, setCurrentLocation] = useState<CurrentLocation | null>(initialLocation);
  const [completionStatus, setCompletionStatus] = useState<Record<string, boolean>>({});
  const [engagedLessonIds, setEngagedLessonIds] = useState<Record<string, true>>({});
  const [isCourseComplete, setIsCourseComplete] = useState(false);
  const [hasCompletedCourse, setHasCompletedCourse] = useState(false);
  const [furthestReachedIndex, setFurthestReachedIndex] = useState<number>(-1);
  const [pendingScrollBlockId, setPendingScrollBlockId] = useState<string | null>(null);
  const [isWaitingForTimeGate, setIsWaitingForTimeGate] = useState(false);
  const [hasWaitedForTimeGate, setHasWaitedForTimeGate] = useState(false);

  // Async paths (hydration, the Continue handler) read the gate through a ref
  // so their callback identities stay stable across the player's 1s refresh
  // ticks — only `allLessonsComplete` depends on the gate identity directly.
  const timeGateRef = useRef(timeGate);
  timeGateRef.current = timeGate;

  // The bootstrap effect below resets every state slice when `course` swaps.
  // Expose a ref so async callbacks (e.g., the LMS reporter) can observe the
  // latest cursor without forcing a re-render.
  const cursorRef = useRef(furthestReachedIndex);
  cursorRef.current = furthestReachedIndex;

  // Stable identity key derived from lesson IDs — survives re-renders that produce
  // a new course object with identical content (e.g. a language switch in the parent).
  const courseKey = JSON.stringify([options.attemptKey, course.lessons.map((l) => l.id), orderedBlockIds]);
  const pacing = useBlockPacing(courseKey);

  // Reset internal state when a genuinely different course is mounted.
  useEffect(() => {
    setCurrentLocation(getFirstLessonLocation(course.lessons));
    setCompletionStatus({});
    setEngagedLessonIds({});
    setIsCourseComplete(false);
    setHasCompletedCourse(false);
    setFurthestReachedIndex(-1);
    setPendingScrollBlockId(null);
    setIsWaitingForTimeGate(false);
    setHasWaitedForTimeGate(false);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [courseKey]);

  const markBlockComplete = useCallback((blockId: string) => {
    setCompletionStatus((current) => ({ ...current, [blockId]: true }));
  }, []);

  const markCurrentLessonEngaged = useCallback(() => {
    const lessonId = currentLocation?.lessonId;
    if (!lessonId) return;
    setEngagedLessonIds((prev) => (prev[lessonId] ? prev : { ...prev, [lessonId]: true }));
  }, [currentLocation]);

  const advanceCursor = useCallback(
    (blockId: string) => {
      const idx = blockIndexById.get(blockId);
      if (idx === undefined) return;
      setFurthestReachedIndex((prev) => (idx > prev ? idx : prev));
    },
    [blockIndexById]
  );

  const clearPendingScrollBlockId = useCallback(() => {
    setPendingScrollBlockId(null);
  }, []);

  const findLessonIdContainingBlockIndex = useCallback(
    (index: number): string | null => {
      if (index < 0) return null;
      let walked = 0;
      for (const lesson of course.lessons) {
        const lessonBlockCount = lesson.blockOrder.filter((id) =>
          blockIndexById.has(id)
        ).length;
        if (index < walked + lessonBlockCount) return lesson.id;
        walked += lessonBlockCount;
      }
      return null;
    },
    [blockIndexById, course.lessons]
  );

  const hydrateProgress = useCallback(
    (
      percentComplete: number,
      options: { completeCourse?: boolean; scrollToCursor?: boolean } = {}
    ) => {
      const total = orderedBlockIds.length;
      if (total === 0) return;

      const completeCourse = options.completeCourse ?? true;
      const scrollToCursor = options.scrollToCursor ?? true;
      const measure = Math.max(0, Math.min(1, percentComplete / 100));
      const targetCursor = gateAwareReachedCount(orderedBlockIds, measure, blockById) - 1;
      if (targetCursor < 0) return;
      const restoredId = orderedBlockIds[targetCursor];
      if (restoredId) pacing.revealThrough(orderedBlockIds, restoredId);

      // Auto-mark every block at-or-before the cursor as complete so lesson
      // gating doesn't trap the learner. Existing completions are preserved.
      setCompletionStatus((prev) => {
        const next = { ...prev };
        for (let i = 0; i <= targetCursor; i++) {
          const id = orderedBlockIds[i];
          if (id) next[id] = true;
        }
        return next;
      });
      setFurthestReachedIndex((prev) => (targetCursor > prev ? targetCursor : prev));

      const lessonId = findLessonIdContainingBlockIndex(targetCursor);
      if (lessonId) {
        setCurrentLocation({ lessonId });
      }
      const cursorBlockId = orderedBlockIds[targetCursor];
      if (scrollToCursor && cursorBlockId) {
        setPendingScrollBlockId(cursorBlockId);
      }
      // Server percent alone is not sufficient for a timed course: the time
      // requirement is re-evaluated against the seeded tracker on relaunch
      // (NA-153) — content-100% with insufficient recorded time resumes
      // incomplete.
      const gate = timeGateRef.current;
      if (completeCourse && measure >= 1 && (!gate || gate.timeGateSatisfied())) {
        setHasCompletedCourse(true);
        setIsCourseComplete(true);
      } else if (completeCourse && measure >= 1 && gate && !gate.timeGateSatisfied()) {
        // Resuming into a content-complete-but-time-unmet state implies the
        // learner already reached the finish point in a prior session.
        setHasWaitedForTimeGate(true);
      }
    },
    [blockById, findLessonIdContainingBlockIndex, orderedBlockIds, pacing]
  );

  const hydrateLessonProgress = useCallback(
    (
      lessonPercentById: Record<string, number>,
      options: {
        completeCourse?: boolean;
        preferredLessonId?: string | null;
        scrollToCursor?: boolean;
      } = {}
    ) => {
      if (orderedBlockIds.length === 0 || course.lessons.length === 0) return;

      const completeCourse = options.completeCourse ?? true;
      const preferredLessonId = options.preferredLessonId ?? null;
      const scrollToCursor = options.scrollToCursor ?? true;
      const nextCompletion: Record<string, true> = {};
      let furthestHydratedIndex = -1;
      let pendingBlockId: string | null = null;

      for (const lesson of course.lessons) {
        const lessonBlockIds = lesson.blockOrder.filter((blockId) => blockIndexById.has(blockId));
        const lessonProgressBlockIds = lessonBlockIds.filter((blockId) => {
          const block = blockById.get(blockId);
          return block ? getBlockCompletionMode(block) !== 'none' : false;
        });
        if (lessonProgressBlockIds.length === 0) continue;

        const percentComplete = Math.max(
          0,
          Math.min(100, lessonPercentById[lesson.id] ?? 0)
        );
        const measure = percentComplete / 100;
        const reachedCount = gateAwareReachedCount(
          lessonProgressBlockIds,
          measure,
          blockById
        );

        const restoredId = lessonProgressBlockIds[reachedCount - 1];
        if (restoredId) pacing.revealThrough(lessonBlockIds, restoredId);

        for (let i = 0; i < reachedCount; i++) {
          const blockId = lessonProgressBlockIds[i];
          if (!blockId) continue;
          nextCompletion[blockId] = true;
          const globalIndex = blockIndexById.get(blockId);
          if (globalIndex !== undefined && globalIndex > furthestHydratedIndex) {
            furthestHydratedIndex = globalIndex;
          }
        }

        if (lesson.id === preferredLessonId) {
          const preferredBlockIndex = Math.max(0, Math.min(reachedCount - 1, lessonProgressBlockIds.length - 1));
          pendingBlockId = scrollToCursor && reachedCount > 0
            ? lessonProgressBlockIds[preferredBlockIndex] ?? null
            : null;
        }
      }

      setCompletionStatus((prev) => ({ ...prev, ...nextCompletion }));
      setFurthestReachedIndex((prev) =>
        furthestHydratedIndex > prev ? furthestHydratedIndex : prev
      );

      if (preferredLessonId && course.lessons.some((lesson) => lesson.id === preferredLessonId)) {
        setCurrentLocation({ lessonId: preferredLessonId });
      } else {
        const lessonId = findLessonIdContainingBlockIndex(furthestHydratedIndex);
        if (lessonId) setCurrentLocation({ lessonId });
      }

      if (pendingBlockId) {
        setPendingScrollBlockId(pendingBlockId);
      }

      const everyLessonHydratedComplete = course.lessons.every(
        (lesson) => (lessonPercentById[lesson.id] ?? 0) >= 100
      );
      // Same time-requirement re-check as hydrateProgress (NA-153): per-lesson
      // percents at 100 don't restore the completed state unless the seeded
      // tracker already satisfies the gate.
      const gate = timeGateRef.current;
      if (everyLessonHydratedComplete && (!gate || gate.timeGateSatisfied())) {
        setHasCompletedCourse(true);
        if (completeCourse) {
          setIsCourseComplete(true);
        }
      } else if (everyLessonHydratedComplete && gate && !gate.timeGateSatisfied()) {
        // Resuming into a content-complete-but-time-unmet state implies the
        // learner already reached the finish point in a prior session.
        setHasWaitedForTimeGate(true);
      }
    },
    [blockById, blockIndexById, course.lessons, findLessonIdContainingBlockIndex, orderedBlockIds, pacing]
  );

  const currentLesson = useMemo(
    () => findLessonByLocation(course.lessons, currentLocation),
    [course.lessons, currentLocation]
  );

  const currentBlocks = useMemo<CourseBlock[]>(() => {
    if (!currentLesson) return [];
    return currentLesson.blockOrder
      .map((blockId) => course.blocks.find((b) => b.id === blockId))
      .filter((block): block is CourseBlock => block !== undefined);
  }, [course.blocks, currentLesson]);

  const lessonProgressMap = useMemo(
    () => buildCourseLessonProgressMap(course, completionStatus, engagedLessonIds),
    [course, completionStatus, engagedLessonIds]
  );

  const navigationLessons = useMemo(
    () => buildLessonNavigationItems(course.lessons),
    [course.lessons]
  );

  const nextLesson = useMemo(
    () => getNextLessonLocation(course.lessons, currentLocation),
    [course.lessons, currentLocation]
  );

  const previousLesson = useMemo(
    () => getPreviousLessonLocation(course.lessons, currentLocation),
    [course.lessons, currentLocation]
  );

  const currentLessonCanProceed = useMemo(() => {
    if (!currentLocation) return false;
    const lesson = course.lessons.find((l) => l.id === currentLocation.lessonId);
    if (!lesson) return false;
    const requiredBlocks = lesson.blockOrder
      .map((blockId) => course.blocks.find((b) => b.id === blockId))
      .filter((b): b is CourseBlock =>
        Boolean(b && blockRequiresCompletion(b, enforceFullVideoView))
      );
    return requiredBlocks.every((b) => completionStatus[b.id] === true);
  }, [course, currentLocation, completionStatus, enforceFullVideoView]);

  // The single point where the time gate affects completion (NA-153): content
  // completion AND the time requirement must both hold. Everything downstream
  // (Finish button, sidebar completed state, the SCORM reporter's completion
  // effect) derives from this. Depends on the gate identity on purpose — the
  // player passes a fresh gate object per refresh tick while unmet, which is
  // what re-evaluates this as time accrues without any click.
  const allLessonsComplete = useMemo(
    () =>
      selectIsCourseComplete(course, completionStatus) &&
      (!timeGate || timeGate.timeGateSatisfied()),
    [course, completionStatus, timeGate]
  );

  const progressMeasure = useMemo(
    () => selectCursorProgressMeasure(course, furthestReachedIndex),
    [course, furthestReachedIndex]
  );

  const canFinishCourse = currentLessonCanProceed && allLessonsComplete;
  const canNavigateNext = nextLesson ? currentLessonCanProceed : canFinishCourse;
  const canNavigatePrev = previousLesson !== null;

  const shouldShowCompletedCourseLessonState = hasCompletedCourse && !isCourseComplete;

  const handleLessonSelect = useCallback(
    (lessonId: string) => {
      setCurrentLocation({ lessonId });
      setIsCourseComplete(false);
      setIsWaitingForTimeGate(false);
    },
    []
  );

  const handlePrevious = useCallback(() => {
    if (!previousLesson) return;
    setCurrentLocation(previousLesson);
    setIsCourseComplete(false);
    setIsWaitingForTimeGate(false);
  }, [previousLesson]);

  const handleNext = useCallback((): boolean => {
    if (!currentLocation) return false;
    if (!canNavigateNext) return false;

    if (nextLesson) {
      setCurrentLocation(nextLesson);
      setIsCourseComplete(false);
      setIsWaitingForTimeGate(false);
      return false;
    }

    setHasCompletedCourse(true);
    setIsCourseComplete(true);
    setIsWaitingForTimeGate(false);
    return true;
  }, [canNavigateNext, currentLocation, nextLesson]);

  const handleLessonCompleteContinue = useCallback((): boolean => {
    // Defense-in-depth: the reveal gate normally keeps the Lesson Complete block
    // (and its Continue button) hidden until required blocks are done, but
    // client-context reordering can place required content after it — so refuse
    // to advance while the current lesson still has an incomplete required block.
    if (!currentLessonCanProceed) return false;

    const nextIncomplete = getNextIncompleteLessonLocation(
      course.lessons,
      currentLocation,
      lessonProgressMap
    );

    if (nextIncomplete) {
      setCurrentLocation(nextIncomplete);
      setIsCourseComplete(false);
      setIsWaitingForTimeGate(false);
      return false;
    }

    // Content is done but the time requirement isn't (NA-153): hold the
    // completion and show the waiting view instead. Checked via the gate
    // directly (not `allLessonsComplete`) so ungated callers keep today's
    // behavior exactly.
    const gate = timeGateRef.current;
    if (gate && !gate.timeGateSatisfied()) {
      setIsWaitingForTimeGate(true);
      setHasWaitedForTimeGate(true);
      return false;
    }

    setHasCompletedCourse(true);
    setIsCourseComplete(true);
    setIsWaitingForTimeGate(false);
    return true;
  }, [course.lessons, currentLocation, lessonProgressMap, currentLessonCanProceed]);

  // The learner already asked to finish (they're on the waiting view); the
  // moment the gate is satisfied, resolve to the completion state without
  // requiring another click.
  useEffect(() => {
    if (isWaitingForTimeGate && allLessonsComplete) {
      setIsWaitingForTimeGate(false);
      setHasCompletedCourse(true);
      setIsCourseComplete(true);
    }
  }, [isWaitingForTimeGate, allLessonsComplete]);

  return {
    pacing,
    currentLocation,
    setCurrentLocation,
    completionStatus,
    markBlockComplete,
    engagedLessonIds,
    markCurrentLessonEngaged,
    currentLesson,
    currentBlocks,
    lessonProgressMap,
    navigationLessons,
    nextLesson,
    previousLesson,
    allLessonsComplete,
    isCourseComplete,
    hasCompletedCourse,
    currentLessonCanProceed,
    canNavigateNext,
    canNavigatePrev,
    shouldShowCompletedCourseLessonState,
    furthestReachedIndex,
    progressMeasure,
    pendingScrollBlockId,
    clearPendingScrollBlockId,
    advanceCursor,
    hydrateProgress,
    hydrateLessonProgress,
    handleLessonSelect,
    handlePrevious,
    handleNext,
    handleLessonCompleteContinue,
    isWaitingForTimeGate,
    hasWaitedForTimeGate,
  };
}
